import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { useObjectStore } from "../zustand/objects";
import { Card, Text, Box } from "@mantine/core";
import { previewCanvasCenterTopCalc } from "../styleFile";
import { PREVIEW_DESTINATION_OUTLINE_COLOR } from "../utils/viewportCanvasDefaults";

/**
 * R3F raycasting uses event.offsetX/Y vs canvas size — same as the center of getBoundingClientRect().
 * Viewport math (100vh, 50%) can drift from that (mobile chrome, subpixels), so we measure the canvas.
 */
const PreviewSplash = ({ canvasElement }) => {
  const { shapeHovered, viewportThemeOverride } = useObjectStore();

  const splashChrome = useMemo(() => {
    const dark = viewportThemeOverride === "dark";
    return dark
      ? {
          standardFill: "#2f2f2f",
          border: "#475569",
          primaryText: "#e2e8f0",
        }
      : {
          standardFill: "#ffffff",
          border: "#e5e7eb",
          primaryText: "#111827",
        };
  }, [viewportThemeOverride]);
  const [splashClicked, setSplashClicked] = useState(false);
  const [pointerLocked, setPointerLocked] = useState(
    () => typeof document !== "undefined" && document.pointerLockElement != null
  );
  const [canvasCenterPx, setCanvasCenterPx] = useState(null);

  useEffect(() => {
    const sync = () => setPointerLocked(document.pointerLockElement != null);
    document.addEventListener("pointerlockchange", sync);
    return () => document.removeEventListener("pointerlockchange", sync);
  }, []);

  useLayoutEffect(() => {
    if (!canvasElement) {
      setCanvasCenterPx(null);
      return undefined;
    }

    const update = () => {
      const r = canvasElement.getBoundingClientRect();
      setCanvasCenterPx({
        x: r.left + r.width / 2,
        y: r.top + r.height / 2,
      });
    };

    update();
    const raf = requestAnimationFrame(update);

    const ro = new ResizeObserver(update);
    ro.observe(canvasElement);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      if (vv) {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
      }
    };
  }, [canvasElement]);

  /** Reticle sits above the canvas; while pointer is locked it must not steal center clicks (teleport / in-scene Html). */
  const reticlePointerEvents =
    splashClicked && pointerLocked ? "none" : "auto";

  const centerStyle =
    canvasCenterPx != null
      ? {
          position: "fixed",
          left: canvasCenterPx.x,
          top: canvasCenterPx.y,
          transform: "translate(-50%, -50%)",
        }
      : {
          position: "fixed",
          left: "50%",
          top: previewCanvasCenterTopCalc,
          transform: "translate(-50%, -50%)",
        };

  return (
    <>
      {!splashClicked && (
        <Card
          data-preview-splash-card
          radius={8}
          p="lg"
          w={{ base: "min(92vw, 400px)", sm: 420 }}
          maw={420}
          style={{
            ...centerStyle,
            zIndex: 100,
            pointerEvents: "auto",
            marginTop: -36,
            border: `1px solid ${splashChrome.border}`,
            boxShadow:
              "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)",
            backgroundColor: splashChrome.standardFill,
          }}
        >
          <Text
            size="sm"
            ta="center"
            lh={1.65}
            style={{ color: splashChrome.primaryText }}
          >
            Click the dot in the center to lock the pointer and look around. Press{" "}
            <Text component="span" fw={600} style={{ color: splashChrome.primaryText }}>
              Esc
            </Text>{" "}
            to show the cursor again.
          </Text>
        </Card>
      )}
      <Box
        id="splashy"
        onClick={() => setSplashClicked(true)}
        style={
          !splashClicked
            ? {
                ...centerStyle,
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: "#111827",
                border: "2px solid #ffffff",
                boxShadow:
                  "0 0 0 1px rgba(255, 255, 255, 0.45), 0 2px 14px rgba(0, 0, 0, 0.35)",
                cursor: "pointer",
                zIndex: 110,
                pointerEvents: reticlePointerEvents,
                transition: "width 0.1s linear, height 0.1s linear, box-shadow 0.1s linear",
              }
            : {
                ...centerStyle,
                width: shapeHovered ? 22 : 10,
                height: shapeHovered ? 22 : 10,
                borderRadius: "50%",
                backgroundColor: shapeHovered ? "transparent" : "#ffffff",
                border: shapeHovered
                  ? `2px solid ${PREVIEW_DESTINATION_OUTLINE_COLOR}`
                  : "2px solid rgba(255, 255, 255, 0.95)",
                boxShadow: shapeHovered
                  ? `0 0 0 1px rgba(254, 154, 0, 0.35)`
                  : "0 0 0 1px rgba(255, 255, 255, 0.4)",
                cursor: pointerLocked ? "default" : "pointer",
                zIndex: 110,
                pointerEvents: reticlePointerEvents,
                transition:
                  "width 0.22s ease, height 0.22s ease, background-color 0.22s ease, border 0.22s ease, box-shadow 0.22s ease",
              }
        }
      />
    </>
  );
};

export default PreviewSplash;
